import { Component, Inject } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
}

const ICONS: Record<ToastType, string> = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const DEFAULT_TITLES: Record<ToastType, string> = {
  success: 'Success',
  error: 'Something went wrong',
  warning: 'Warning',
  info: 'Info',
};

/**
 * Toast shown by ToastService (top centre, via MatSnackBar.openFromComponent):
 * coloured icon, title, message, close button and a countdown bar.
 */
@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <div class="app-toast" [class]="'app-toast app-toast--' + data.type" role="status" aria-live="polite">
      <span class="app-toast__icon"><mat-icon>{{ icon }}</mat-icon></span>
      <div class="app-toast__text">
        <div class="app-toast__title">{{ title }}</div>
        @if (data.message) {
        <div class="app-toast__message">{{ data.message }}</div>
        }
      </div>
      <button type="button" class="app-toast__close" (click)="ref.dismiss()" aria-label="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
      @if (data.duration > 0) {
      <span class="app-toast__bar" [style.animation-duration.ms]="data.duration"></span>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      /* dark card: readable on any page, coloured accents per type */
      .app-toast {
        --toast-accent: #60a5fa;
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 240px;
        max-width: min(460px, calc(100vw - 32px));
        padding: 8px 6px 9px 8px;
        border-radius: 10px;
        background: #111827;
        box-shadow: 0 20px 45px -12px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.06) inset;
        overflow: hidden;
        color: #f9fafb;
        font-family: inherit;
        animation: app-toast-in 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.2);
      }

      .app-toast--success { --toast-accent: #22c55e; }
      .app-toast--error   { --toast-accent: #ef4444; }
      .app-toast--warning { --toast-accent: #f59e0b; }
      .app-toast--info    { --toast-accent: #60a5fa; }

      .app-toast__icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 7px;
        background: var(--toast-accent);
        color: #fff;
      }

      .app-toast__icon mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: inherit !important;
      }

      .app-toast__text {
        flex: 1;
        min-width: 0;
      }

      .app-toast__title {
        font-size: 13px;
        font-weight: 700;
        line-height: 1.25;
        color: #fff;
      }

      .app-toast__message {
        margin-top: 1px;
        font-size: 12.5px;
        line-height: 1.35;
        color: #d1d5db;
        word-break: break-word;
      }

      .app-toast__close {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #9ca3af;
        cursor: pointer;
      }

      .app-toast__close:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
      }

      .app-toast__close mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: inherit !important;
      }

      /* time left before it closes */
      .app-toast__bar {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: var(--toast-accent);
        transform-origin: left;
        animation: app-toast-countdown linear forwards;
      }

      @keyframes app-toast-countdown {
        from { transform: scaleX(1); }
        to { transform: scaleX(0); }
      }

      @keyframes app-toast-in {
        from { opacity: 0; transform: translateY(-12px) scale(0.98); }
        to { opacity: 1; transform: none; }
      }
    `,
  ],
})
export class AppToastComponent {
  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: ToastData,
    public ref: MatSnackBarRef<AppToastComponent>
  ) {}

  get icon(): string {
    return ICONS[this.data.type] || 'info';
  }

  /** The caller's second argument ("Success", "Failed", "Warning"...) is the title. */
  get title(): string {
    return this.data.title || DEFAULT_TITLES[this.data.type];
  }
}
